import { useEffect, useRef, useState } from 'react';

/**
 * Custom hook for 3D animation effects based on mouse movement
 * Returns ref to attach to element and transform styles
 */
export const use3DAnimation = (options = {}) => {
  const {
    maxRotation = 15,
    scale = 1.05,
    transition = 'all 0.3s ease-out',
    perspective = 1000,
    disableOnTouch = true,
  } = options;

  const elementRef = useRef(null);
  const [transform, setTransform] = useState('');
  const [isTouchLikeDevice, setIsTouchLikeDevice] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const touchMediaQuery = window.matchMedia('(hover: none), (pointer: coarse)');

    const updateTouchState = () => {
      setIsTouchLikeDevice(touchMediaQuery.matches);
    };

    updateTouchState();
    touchMediaQuery.addEventListener('change', updateTouchState);

    return () => {
      touchMediaQuery.removeEventListener('change', updateTouchState);
    };
  }, []);

  useEffect(() => {
    if (disableOnTouch && isTouchLikeDevice) {
      return;
    }

    const element = elementRef.current;
    if (!element) return;

    const handleMouseMove = (e) => {
      const rect = element.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      
      const mouseX = e.clientX - centerX;
      const mouseY = e.clientY - centerY;
      
      const rotateX = (mouseY / (rect.height / 2)) * -maxRotation;
      const rotateY = (mouseX / (rect.width / 2)) * maxRotation;
      
      setTransform(
        `perspective(${perspective}px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(${scale})`
      );
    };

    const handleMouseLeave = () => {
      setTransform('perspective(1000px) rotateX(0deg) rotateY(0deg) scale(1)');
    };

    element.addEventListener('mousemove', handleMouseMove);
    element.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      element.removeEventListener('mousemove', handleMouseMove);
      element.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [maxRotation, scale, perspective, disableOnTouch, isTouchLikeDevice]);

  const computedTransform = disableOnTouch && isTouchLikeDevice ? 'none' : transform;

  return {
    ref: elementRef,
    style: {
      transform: computedTransform,
      transition: disableOnTouch && isTouchLikeDevice ? 'none' : transition,
      transformStyle: 'preserve-3d',
    },
  };
};

/**
 * Custom hook for floating 3D animation
 * Creates a continuous floating effect
 */
export const useFloating3D = () => {
  return {
    style: {
      animation: 'floating3d 6s ease-in-out infinite',
      transformStyle: 'preserve-3d',
    },
  };
};
